import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Input } from '../Input';
import { FormLabel } from '../FormLabel';
import { InteractiveListItem } from '../InteractiveList/InteractiveList';
import { client } from '../../api';
import { formatTin } from '../../utils';
import * as Sentry from '@sentry/react';
import { type OfficeDetail } from './types';

type EditOfficeModalProps = {
  /** Called when the modal should close. */
  onClose: () => void;
  /** All office rows in this group (same name/TIN). */
  matchedAddresses: OfficeDetail[];
};

const EditOfficeModal: React.FC<EditOfficeModalProps> = ({ onClose, matchedAddresses }) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState(matchedAddresses[0]?.name ?? '');
  const [tin, setTin] = useState(matchedAddresses[0]?.tin ?? '');
  const [remainingOffices, setRemainingOffices] = useState<OfficeDetail[]>(matchedAddresses);
  const [removedIds, setRemovedIds] = useState<string[]>([]);

  const handleRemoveAddress = (officeId: string) => {
    setRemainingOffices((prev) => prev.filter((o) => o.id !== officeId));
    setRemovedIds((prev) => [...prev, officeId]);
  };

  const { mutate: saveChanges, isPending } = useMutation({
    mutationFn: async () => {
      if (removedIds.length > 0) {
        await client.post('/settings/offices/bulk-delete', { office_ids: removedIds });
      }

      if (remainingOffices.length > 0) {
        await client.patch('/settings/offices/bulk-update', {
          office_ids: remainingOffices.map((office) => office.id),
          name,
          tin: tin || null,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offices-v2'] });
      queryClient.invalidateQueries({ queryKey: ['unmatched-addresses'] });
      toast.custom(() => <Toast variant="success">Office updated</Toast>);
      onClose();
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const hasChanges =
    removedIds.length > 0 || name !== matchedAddresses[0]?.name || tin !== (matchedAddresses[0]?.tin ?? '');

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Edit office</ModalTitle>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <Input
            label="Office name"
            placeholder="e.g. Downtown office"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />

          <Input
            label="TIN"
            placeholder="987-65-4321"
            value={tin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTin(formatTin(e.target.value))}
          />

          <div className="flex flex-col gap-1">
            <FormLabel>Office address</FormLabel>
            {remainingOffices.map((office) => (
              <InteractiveListItem
                key={office.id}
                removeButtonProps={{
                  onClick: () => handleRemoveAddress(office.id),
                  title: `Remove ${office.address}`,
                  disabled: remainingOffices.length <= 1,
                }}
              >
                <Typography styledAs="bodyDMSans">{office.address}</Typography>
              </InteractiveListItem>
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} disabled={isPending}>
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!name.trim() || !hasChanges}
          isLoading={isPending}
          onClick={() => saveChanges()}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditOfficeModal;
